import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Play, Send } from "lucide-react";
import TheField from "@/components/TheField";
import BreathRing from "@/components/BreathRing";
import { api } from "@/lib/apiClient";
import { playUrl, stopAudio } from "@/lib/sharedAudio";
import { primeBreath } from "@/lib/breathSound";
import { shareReset } from "@/lib/share";
import { InviteButton } from "@/components/InviteFriend";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Adelaida guides the reset; the words and the breathing ring follow her voice line by line.
export default function ResetPlayer({ reset, onClose, next, load, onDone }) {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const coach = ["onyx", "echo", "ash", "fable"].includes(user?.voice) ? "Richard" : "Adelaida";
  const [t, setT] = useState(0);
  const [done, setDone] = useState(false);
  const [needTap, setNeedTap] = useState(false);
  const [failed, setFailed] = useState(false);

  const start = (d) => {
    setNeedTap(false); primeBreath();
    playUrl(`${BACKEND_URL}${d.url}`, { onTime: (p) => setT(p.currentTime), onEnded: () => { setDone(true); onDone?.(); } }).catch(() => setNeedTap(true));
  };
  useEffect(() => {
    let off = false;
    (load ? load().then((d) => ({ data: d })) : api.post(`/resets/${reset.id}/play`, {})).then(({ data: d }) => { if (!off) { setData(d); start(d); } }).catch(() => !off && setFailed(true));
    return () => { off = true; stopAudio(); };
  }, [reset.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const idx = data ? data.script.reduce((a, l, i) => (l.start <= t ? i : a), -1) : -1;
  const line = idx >= 0 && !done ? data.script[idx].text : "";
  const shown = done ? data.close : line;

  return (
    <div className="fixed inset-0 z-[80] bg-background/95 backdrop-blur-2xl grain flex flex-col items-center justify-center px-6" data-testid={`reset-${reset.id}-experience`}>
      <button onClick={onClose} className="absolute top-5 right-5 w-11 h-11 rounded-full glass flex items-center justify-center text-muted-foreground hover:text-foreground" data-testid="reset-close-btn"><X className="w-5 h-5" /></button>
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-10">{reset.title}</p>
      <BreathRing line={line}><TheField intensity={0.8} size={200} active><span /></TheField></BreathRing>
      <AnimatePresence mode="wait">
        <motion.p key={shown || "wait"} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 0.95, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.9 }}
          className="font-serif text-2xl sm:text-3xl text-center italic text-foreground/90 max-w-md mt-16 min-h-[5rem]" data-testid="reset-phrase">
          {failed ? "This reset couldn't load. Please try again in a moment."
            : !data ? <span className="inline-flex items-center gap-2 text-base not-italic text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {coach} is getting ready…</span>
            : shown}
        </motion.p>
      </AnimatePresence>
      {needTap && data && !done && (
        <button onClick={() => start(data)} className="mt-6 px-6 py-3 rounded-full bg-primary text-primary-foreground inline-flex items-center gap-2" data-testid="reset-begin-btn"><Play className="w-4 h-4" /> Begin</button>
      )}
      {done && (
        <div className="mt-8 flex flex-col items-center gap-4">
          <button onClick={onClose} className="px-8 py-3 rounded-full bg-primary text-primary-foreground" data-testid="reset-done-btn">Done</button>
          {next && <button onClick={next.onGo} className="text-sm text-primary" data-testid="reset-next-btn">{next.label} →</button>}
          {!load && <InviteButton className="text-sm text-primary inline-flex items-center gap-1.5" testid="reset-invite-btn" />}
          {!load && <button onClick={() => shareReset(reset)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="reset-share-btn"><Send className="w-3.5 h-3.5" /> Send this to a friend</button>}
        </div>
      )}
    </div>
  );
}
